<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { flashMessage } from '@/utils/FlashMessages'
import { versionService } from '@/core/services'
import Version from '@/core/resources/Version'
import { useStore } from '@/core/store'
import { popupUtil } from '@/utils/Popup'
import { handleError } from '@/utils/ErrorHandler'
import { useRoute } from 'vue-router'
import router from '@/router'
import TextInput from '@/components/input/TextInput.vue'

const store = useStore()
const version = ref<Version>(new Version())
const file = ref<File | null>(null)
const route = useRoute()
const id = ref(route.params.id)

onMounted(() => {
  if (!id.value) return

  versionService
    .get(route.params.id as string, store.getAccessToken())
    .then((r) => {
      version.value = r
    })
    .catch(() => {
      router.push({ name: 'versions' })
    })
})

const create = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to create this version?')

  if (!confirmed) return

  versionService
    .create(version.value, store.getAccessToken())
    .then((version) => {
      if (file.value) versionService.uploadUsersGuide(version, file.value, store.getAccessToken())
    })
    .then(() => {
      flashMessage.success('Version created successfully')
      router.push({ name: 'versions' })
    })
    .catch(handleError)
}

const edit = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to update this version?')

  if (!confirmed) return

  versionService
    .update(version.value, store.getAccessToken())
    .then((version) => {
      if (file.value) versionService.uploadUsersGuide(version, file.value, store.getAccessToken())
    })
    .then(() => {
      flashMessage.success('Version updated successfully')
      router.push({ name: 'collections' })
    })
    .catch(handleError)
}
</script>

<template>
  <div class="container">
    <form novalidate>
      <h3 v-if="id">Edit version</h3>
      <h3 v-else>Create version</h3>

      <div class="form-group">
        <TextInput
          v-model="version.name"
          name="versionname"
          id="versionname"
          placeholder="Version name"
          required
          type="text"
          :disabled="id ? true : false" />

        <h4>Description</h4>
        <textarea v-model="version.description" maxlength="100"></textarea>
        <TextInput v-model="version.doi" name="doi" id="doi" placeholder="DOI" type="text" maxlength="25" />

        <h4>User's guide (pdf)</h4>
        <input type="file" accept=".pdf" @change="file = ($event.target as HTMLInputElement).files![0]" />
      </div>

      <button v-if="id" type="button" class="submit-button" @click="edit">Update version</button>
      <button v-else type="button" class="submit-button" @click="create">Create version</button>
    </form>
  </div>
</template>

<style scoped lang="scss">
textarea {
  height: 100px;
  background-color: var(--theme-background);
  border: 1px solid var(--theme-border-color);
  border-radius: 8px;
  padding: 8px;
  font-size: 1rem;
  resize: none;
}

@import '@/assets/styles/input.scss';
@import '@/assets/styles/form.scss';
</style>
